<script setup>
import { ref, computed, onMounted } from 'vue'
import { useQuasar } from 'quasar'
import { useWebSiteStore } from '@/stores/website.store'
import { useNotify } from '@/composables/notify'
import { deleteSubscriptionAsync, activateAutoRenewalAsync, getUpdatePaymentMethodTransactionAsync, getProductsAsync } from '@/apis/subscriptions.api'
import { usePaddle } from '@/composables/paddle'
import i18n from '@/plugins/i18n'
import SubscriptionForm from '@/components/subscription/SubscriptionForm.vue'
import { useDetectDevice } from '@/composables/device'

// --- 1. Init & Composables ---
const t = i18n.global.t
const $q = useQuasar()
const { notifyError } = useNotify()
const websiteStore = useWebSiteStore()
const { init, isReady, openCheckout } = usePaddle()
const { isMobile } = useDetectDevice()

// --- 2. State & Computed ---
const isLoadingAction = ref(false)
const editableQuantity = ref(null)
const availableUpgrades = ref([])
const showCheckout = ref(false)
const showUpgradeDialog = ref(false)
const tutorialUrl = ref('https://zaplater.com')

const currentSubscription = computed(() => websiteStore.activeSubscription)

const cancelled = computed(() => {
  if (!currentSubscription.value) return false
  return currentSubscription.value.expiring_at !== null
})

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const options = { year: 'numeric', month: 'short', day: 'numeric' }
  return new Date(dateStr).toLocaleDateString(undefined, options)
}

// --- 3. API Actions ---
const onCancelAutoRenewal = async() => {
  if (!currentSubscription.value) return

  isLoadingAction.value = true
  try {
    await deleteSubscriptionAsync(currentSubscription.value.id)
    await websiteStore.fetchSubscriptions()
  } catch (error) {
    notifyError(t('subscriptions.manage.cancelFailed'))
  } finally {
    isLoadingAction.value = false
  }
}

const onActivateAutoRenewal = async() => {
  if (!currentSubscription.value) return

  isLoadingAction.value = true
  try {
    await activateAutoRenewalAsync(currentSubscription.value.id)
    await websiteStore.fetchSubscriptions()
  } catch (error) {
    notifyError(t('subscriptions.manage.activateFailed'))
  } finally {
    isLoadingAction.value = false
  }
}

const onUpdatePaymentMethod = async() => {
  if (!currentSubscription.value) return
  if (!isReady.value) return

  isLoadingAction.value = true
  try {
    showCheckout.value = true
    const response = await getUpdatePaymentMethodTransactionAsync(currentSubscription.value.id)
    const { transaction_id } = response.data
    openCheckout(transaction_id)
  } catch (error) {
    console.error('Error updating payment method:', error)
    notifyError(t('subscriptions.manage.updateCardFailed'))
    showCheckout.value = false
  } finally {
    isLoadingAction.value = false
  }
}

// --- 4. Dialogs / Handlers ---
const confirmCancelRenewal = () => {
  $q.dialog({
    title: t('subscriptions.manage.confirmCancelTitle'),
    message: t('subscriptions.manage.confirmCancelMessage'),
    cancel: {
      label: t('common.cancel'),
      color: 'grey-7',
      flat: true
    },
    ok: {
      label: t('common.confirm'),
      color: 'warning',
      unelevated: true
    },
    persistent: true
  }).onOk(() => {
    onCancelAutoRenewal()
  })
}

const confirmActivateRenewal = () => {
  $q.dialog({
    title: t('subscriptions.manage.confirmActivateTitle'),
    message: t('subscriptions.manage.confirmActivateMessage'),
    cancel: {
      label: t('common.cancel'),
      color: 'grey-7',
      flat: true
    },
    ok: {
      label: t('common.confirm'),
      color: 'primary',
      unelevated: true
    },
    persistent: true
  }).onOk(() => {
    onActivateAutoRenewal()
  })
}

const handleSubscriptionUpgraded = async() => {
  showUpgradeDialog.value = false
  await websiteStore.fetchSubscriptions()
  $q.notify({
    type: 'positive',
    message: t('subscriptions.success.upgraded')
  })
}

// --- 5. Lifecycle ---
onMounted(async() => {
  init()
  editableQuantity.value = currentSubscription.value?.quantity || 1

  try {
    const res = await getProductsAsync(currentSubscription.value?.product_rank)
    availableUpgrades.value = res?.data?.results || []
  } catch (error) {
    console.error('Error fetching available upgrades:', error)
  }

  try {
    const res = await websiteStore.getAppSettingAsync('TUTORIAL_URL')
    tutorialUrl.value = res || tutorialUrl.value
  } catch (error) {
    console.error('Error fetching tutorial URL:', error)
  }
})
</script>

<template>
  <q-page class="q-pa-md  ">
    <!-- Checkout View -->
    <div v-show="showCheckout">
      <div class="row items-center q-mb-md">
        <q-btn
          flat
          dense
          color="primary"
          icon="mdi-arrow-left"
          :label="$t('common.back')"
          @click="() => showCheckout = false"
        />
      </div>

      <div class="checkout-container" />
    </div>
    <div v-show="!showCheckout">
      <div class="q-gutter-y-md">
        <div class="q-mb-md">
          <div class="text-h5 text-weight-bold">
            {{ $t('subscriptions.manage.title') }}
          </div>
          <div class="text-subtitle2 text-grey-7">
            {{ $t('subscriptions.manage.subtitle') }}
          </div>
        </div>
        <div v-if="currentSubscription">
          <!-- Past Due Warning -->
          <q-banner
            v-if="websiteStore.isPastDueSubscription"
            dense
            rounded
            inline-actions
            class="bg-warning text-white q-mb-sm"
          >
            <template #left>
              <q-icon name="mdi-alert" />
            </template>

            <div class="text-body2 text-weight-bold">
              {{ $t('subscriptions.manage.pastDueWarning1') }}
            </div>
            <div class="text-caption q-mt-xs">
              {{ $t('subscriptions.manage.pastDueWarning2') }}
            </div>
          </q-banner>
          <!-- Info Toggle Button -->
          <q-btn
            flat
            dense
            icon="mdi-information-outline"
            @click="websiteStore.viewInfoBanner = !websiteStore.viewInfoBanner"
          />

          <!-- Navigation Infos -->
          <q-banner
            v-if="websiteStore.viewInfoBanner"
            class="info_menu q-mb-sm"
            dense
            rounded
          >
            <div class="text-body2">
              <div class="text-weight-medium q-mb-xs">
                {{ $t('subscriptions.manage.tutorialIntro') }}
              </div>
              <ol
                class="q-pl-md q-mb-none q-mt-xs"
                style="line-height: 1.8;"
              >
                <li>{{ $t('subscriptions.manage.tutorialStep1') }}</li>
                <li>{{ $t('subscriptions.manage.tutorialStep2') }}</li>
                <li>{{ $t('subscriptions.manage.tutorialStep3') }}</li>
                <li>{{ $t('subscriptions.manage.tutorialStep4') }}</li>
                <li>{{ $t('subscriptions.manage.tutorialStep5') }}</li>
                <li>
                  {{ $t('subscriptions.manage.tutorialLink') }} <a
                    :href="tutorialUrl"
                    target="_blank"
                  >{{ $t('subscriptions.manage.tutorialLinkText') }}</a>
                </li>
              </ol>
            </div>
          </q-banner>

          <!-- Current Plan -->
          <q-card class="q-mb-sm">
            <q-card-section class="q-pa-md">
              <div class="row items-center justify-between q-mb-sm">
                <div>
                  <div class="text-h6">
                    {{ currentSubscription?.product_name }} - {{ currentSubscription?.billing_cycle_frequency }}
                  </div>
                  <div
                    v-if="!cancelled"
                    class="text-body2 text-grey-8"
                  >
                    {{ $t('subscriptions.manage.nextBilling') }}:
                    <span class="text-weight-medium">{{ formatDate(currentSubscription?.next_billed_at) }}</span>
                  </div>
                  <div
                    v-else
                    class="text-body2 text-grey-8"
                  >
                    {{ $t('subscriptions.manage.subExpires') }}:
                    <span class="text-weight-medium">{{ formatDate(currentSubscription?.expiring_at) }}</span>
                  </div>
                </div>
              </div>
            </q-card-section>
          </q-card>

          <!-- Payment Method -->
          <q-card class="q-mb-sm">
            <q-card-section class="q-pa-md">
              <div class="row items-center justify-between">
                <div class="text-h6">
                  {{ $t('subscriptions.manage.paymentMethod') }}
                </div>
                <q-icon
                  name="mdi-credit-card-outline"
                  size="1.5em"
                />
              </div>

              <div class="row items-center justify-between no-wrap">
                <div class="row items-center q-gutter-x-sm">
                  <div v-if="currentSubscription.card_last4">
                    <div class="text-weight-medium text-capitalize">
                      {{ currentSubscription.card_brand }} **** {{ currentSubscription.card_last4 }}
                    </div>
                    <div class="text-caption text-grey-7">
                      {{ $t('subscriptions.manage.cardExpires') }} {{ currentSubscription.card_exp_month }}/{{ currentSubscription.card_exp_year }}
                    </div>
                  </div>
                  <div v-else>
                    <div class="text-weight-medium">
                      {{ $t('subscriptions.manage.savedPaymentMethod') }}
                    </div>
                    <div class="text-caption text-grey-7">
                      {{ $t('subscriptions.manage.secureBilling') }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="row justify-end q-mt-md">
                <q-btn
                  outline
                  color="primary"
                  :loading="isLoadingAction"
                  @click="onUpdatePaymentMethod"
                >
                  {{ $t('subscriptions.manage.updateCard') }}
                </q-btn>
              </div>
            </q-card-section>
          </q-card>

          <!-- Upgrade Plan -->
          <q-card class="q-mb-sm">
            <q-card-section class="q-pa-md">
              <div class="row items-center justify-between q-mb-sm">
                <div class="text-h6">
                  {{ $t('subscriptions.manage.upgradeTitle') }}
                </div>
                <q-icon
                  name="mdi-rocket-launch-outline"
                  size="1.5em"
                />
              </div>

              <div class="text-caption text-grey-7">
                {{ $t('subscriptions.manage.upgradeHint') }}
              </div>

              <div class="row justify-end q-mt-md">
                <q-btn
                  outline
                  color="primary"
                  :loading="isLoadingAction"
                  @click="() => showUpgradeDialog = true"
                >
                  {{ $t('common.upgrade') }}
                </q-btn>
              </div>
            </q-card-section>
          </q-card>

          <!-- Cancel/Activate Renewal  -->
          <q-card class="q-mb-sm">
            <q-card-section class="q-pa-md">
              <div v-if="!cancelled">
                <div class="text-h6">
                  {{ $t('subscriptions.manage.cancelRenewalTitle') }}
                </div>
                <div class="text-caption text-grey-7">
                  {{ $t('subscriptions.manage.cancelRenewalHint') }}
                </div>
                <div class="row justify-end q-mt-md">
                  <q-btn
                    outline
                    color="warning"
                    :loading="isLoadingAction"
                    @click="confirmCancelRenewal"
                  >
                    {{ $t('subscriptions.manage.cancelRenewalButton') }}
                  </q-btn>
                </div>
              </div>

              <div v-else>
                <div class="text-h6">
                  {{ $t('subscriptions.manage.activateRenewalTitle') }}
                </div>
                <div class="text-caption text-grey-7">
                  {{ $t('subscriptions.manage.activateRenewalHint') }}
                </div>
                <div class="row justify-end q-mt-md">
                  <q-btn
                    outline
                    color="primary"
                    :loading="isLoadingAction"
                    @click="confirmActivateRenewal"
                  >
                    {{ $t('subscriptions.manage.activateRenewalButton') }}
                  </q-btn>
                </div>
              </div>
            </q-card-section>
          </q-card>
        </div>
      </div>

      <!-- Upgrade Dialog -->
      <q-dialog
        v-model="showUpgradeDialog"
        :full-width="true"
        :full-height="true"
        :maximized="isMobile"
      >
        <q-card
          v-touch-swipe.mouse.right="() => showUpgradeDialog = false"
          class="dialog-card"
        >
          <q-card-section class="row items-center q-pb-none">
            <div class="text-h6">
              {{ $t('subscriptions.manage.upgradeTitle') }} : {{ currentSubscription?.product_name }} - {{ currentSubscription?.billing_cycle_frequency }}
            </div>
            <q-space />
            <q-btn
              v-close-popup
              icon="mdi-close"
              flat
              round
              dense
            />
          </q-card-section>
          <q-card-section>
            <SubscriptionForm
              :current-subscription="currentSubscription"
              :is-upgrade="true"
              @subscription-upgraded="handleSubscriptionUpgraded"
            />
          </q-card-section>
        </q-card>
      </q-dialog>
    </div>
  </q-page>
</template>

